<template>
    <div class="relative w-full max-w-full overflow-hidden bg-white dark:bg-slate-800 rounded-3xl shadow-lg ring-1 ring-black/5 group flex items-center">
        <!-- Background Image Component -->
        <BackgroundImageWithOverlay :image="cover" />

        <!-- Content -->
        <div class="relative z-10 w-full lg:w-1/2 p-4 md:p-6 md:pl-12 flex flex-col justify-center min-w-0 max-w-full">
            <p class="text-xl sm:text-2xl md:text-3xl font-bold text-gray-700 dark:text-gray-200 leading-tight opacity-90 break-words">{{ name[0] }}</p>
            <h1 class="text-2xl sm:text-4xl md:text-6xl font-black text-gray-900 dark:text-white leading-tight sm:leading-none tracking-tight uppercase break-words hyphens-auto">{{ name[1] }}</h1>
            <p v-if="description" class="text-gray-500 dark:text-gray-400 mt-3 sm:mt-4 max-w-md text-sm font-medium break-words">{{ description }}</p>
            
            <div class="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 sm:mt-8 max-w-full">
                <!-- Play Button -->
                <Tooltip position="bottom" :text="$t('playlist.play')">
                    <button 
                        type="button"
                        @click="onPlay"
                        class="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-black transition-all hover:scale-105 active:scale-95 shadow-lg group-hover:shadow-xl border-none cursor-pointer text-sm sm:text-base shrink-0"
                    >
                        <PlayIcon class="w-5 h-5 sm:w-6 sm:h-6" />
                        <span class="font-bold">{{ $t('playlist.play') }}</span>
                    </button>
                </Tooltip>

                <!-- Actions Group -->
                <div class="flex items-center gap-1 sm:gap-2 shrink-0" 
                    v-if="favoriteVisible || shareVisible || editVisible">
                    <Tooltip position="bottom" :text="$t('playlist.favorite')">
                        <button v-if="favoriteVisible" 
                            type="button"
                            @click="onFavorite"
                            class="p-2.5 sm:p-3 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 hover:text-yellow-300 transition-colors cursor-pointer border-none"
                            :title="$t('playlist.favorite')"
                            :aria-label="$t('playlist.favorite')"
                        >
                            <StarIcon class="w-5 h-5 sm:w-6 sm:h-6" :class="{ 'fill-current text-yellow-300': props.playlist?.isFavorite }" />
                        </button>
                    </Tooltip>
                    <Tooltip position="bottom" :text="$t('playlist.share')">
                        <button v-if="shareVisible" 
                            type="button"
                            @click="onShare"
                            class="p-2.5 sm:p-3 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer border-none"
                            :title="$t('playlist.share')"
                            :aria-label="$t('playlist.share')"
                        >
                            <ShareIcon class="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                    </Tooltip>
                    <Tooltip position="bottom" :text="$t('playlist.edit')">
                        <button v-if="editVisible" 
                            type="button"
                            @click="onEdit"
                            class="p-2.5 sm:p-3 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer border-none"
                            :title="$t('playlist.edit')"
                            :aria-label="$t('playlist.edit')"
                        >
                            <PencilIcon class="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                    </Tooltip>
                </div>
            </div>

            <!-- Tags -->
            <div class="mt-6">
                <TagsViewer :tags="tags" :visibleAddButton="false" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, computed, defineEmits, watch, ref, onMounted } from 'vue'
import { PlayIcon, StarIcon, ShareIcon, PencilIcon } from '@heroicons/vue/24/solid'
import BackgroundImageWithOverlay from '@/components/common/BackgroundImageWithOverlay.vue'
import mediaService from '@/service/media.service'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import TagsViewer from '@/components/common/elements/TagsViewer.vue';

const props = defineProps({
    playlist: {
        type: Object,
        required: true
    },
    favoriteVisible: {
        type: Boolean,
        default: false
    },
    shareVisible: {
        type: Boolean,
        default: false
    },
    editVisible: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['play', 'share', 'edit', 'favorite'])

const cover = ref(props.playlist.cover)

watch(() => props.playlist, async () => {
    checkCover()
})

const checkCover = async () => {
    if (props.playlist.cover) {
        cover.value = props.playlist.cover
        return
    }
    cover.value = await mediaService.fetchSuggestedImage(props.playlist)
}

onMounted(async () => {
    checkCover()
})  

const name = computed(() => {
    if (!props.playlist?.name) return ['', '']
    const words = props.playlist.name.split(' ')
    if (words.length === 1) return [words[0], '']
    
    // Split roughly in half, biased towards second line having more impact if possible, 
    // or balanced. Let's try balanced first.
    const mid = Math.ceil(words.length / 2)
    const firstHalf = words.slice(0, mid).join(' ')
    const secondHalf = words.slice(mid).join(' ')
    
    return [firstHalf, secondHalf]
});

const description = computed(() => {
    return props.playlist?.description || ''
})

const tags = computed(() => {
    return props.playlist?.tags || []
})

const onEdit = () => {
    emit('edit', props.playlist)
}

const onFavorite = () => {
    emit('favorite', props.playlist)
}

const onShare = () => {
    emit('share', props.playlist)
}

const onPlay = () => {
    emit('play', props.playlist)
}

</script>
